import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { UserCard } from '@shared/components/ui/usercard';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { statusColorMap } from '../config/orderStatusConfig';
import { Order } from '../models/Orders';
import { PaymentMethodsApiAdapter } from '../adapters/PaymentMethodsApiAdapter';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

// 🎯 Función para convertir ColDef de AG Grid a ColumnDef de TanStack para ColumnSelector
const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<Order>[] => {
  return agGridColumns
    .filter((col) => {
      // 🎯 Excluir columnas que no deben ser opcionales
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
      cell: col.cellRenderer ? () => null : undefined, // ColumnSelector no necesita cell renderers
    }));
};

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  hasTeamData?: boolean;
}

export const useAgGridColumns = ({ selectedColumns = [], hasTeamData = false }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('ordersShippedModule');

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const paymentMethodsAdapter = useMemo(() => new PaymentMethodsApiAdapter(httpClient), [httpClient]);

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['orders-shipped-payment-methods'],
    queryFn: () => paymentMethodsAdapter.getAll(),
    staleTime: 5 * 60 * 1000,
  });

  const paymentMethodMap = useMemo(() => {
    const map = new Map<string, string>();
    paymentMethods.forEach((m) => map.set(m.id, m.name));
    return map;
  }, [paymentMethods]);

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('table.order'),
        sortable: true,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <div className="text-sm font-medium flex items-center gap-2">{order.ref}</div>;
        },
      },

      {
        headerName: t('table.products'),
        field: 'products',
        valueGetter: (params) => params.data.lines?.length || 0,
      },

      {
        headerName: t('table.shop'),
        field: 'shop',
        sortable: true,
        valueGetter: (params) => params.data.shop?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          if (!order.shop) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return <ShopComponent name={order.shop.name} src={order.shop.image_hash} />;
        },
      },

      {
        headerName: t('table.status'),
        field: 'status',
        valueGetter: (params) => params.data.shipment?.status_mapped?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          const statusMapped = order.shipment?.status_mapped;

          if (!statusMapped) {
            return <Badge className="text-xs bg-card-border text-text">{t('table.noLogisticStatus')}</Badge>;
          }

          {
            /* Mapear colores basados en el ID del status_mapped - Map colors based on status_mapped ID */
          }
          const color = statusColorMap[statusMapped.id] || {
            bg: 'bg-card-border',
            text: 'text-text',
          };

          return <Badge className={`text-xs ${color.bg} ${color.text}`}>{statusMapped.name}</Badge>;
        },
      },

      // 🔵 COLUMNA: Courier
      {
        headerName: t('table.courier'),
        field: 'courier',
        valueGetter: (params) => params.data.shipment?.courier?.name,
        // 🎯 CELDA: Componente Shop con imagen del courier
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return order.shipment?.courier ? (
            <ShopComponent name={order.shipment.courier.name} src={order.shipment.courier.image_hash} />
          ) : (
            <span className="text-sm">-</span>
          );
        },
      },

      // 🔵 COLUMNA: Dirección
      {
        headerName: t('table.address'),
        field: 'address',
        valueGetter: (params) => {
          const address = params.data.customer?.address;
          return address ? `${address.zip}, ${address.city}` : '-';
        },
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          const address = order.customer?.address;
          const fullAddress = address ? `${address.zip}, ${address.city}` : '-';

          return <span className="text-sm font-medium">{fullAddress}</span>;
        },
      },

      {
        headerName: t('table.date_tracking_update'),
        field: 'date_tracking_update',
        sortable: true,
        valueGetter: (params) => params.data.shipment?.date_tracking_update,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{formatDate(order.shipment?.date_tracking_update)}</span>;
        },
      },

      {
        headerName: t('table.processed_at'),
        field: 'processed_at',
        sortable: true,
        valueGetter: (params) => params.data.shipment?.processed_at,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm">{formatDate(order.shipment?.processed_at)}</span>;
        },
      },

      {
        headerName: t('table.tracking_number'),
        field: 'tracking_number',
        valueGetter: (params) => params.data.shipment?.tracking_number,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return <span className="text-sm font-mono">{order.shipment?.tracking_number || '-'}</span>;
        },
      },

      {
        headerName: t('table.payment_method'),
        field: 'payment_method',
        valueGetter: (params) => {
          const order = params.data as Order;
          return paymentMethodMap.get(String(order.payment_method_id)) || '-';
        },
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          const name = paymentMethodMap.get(String(order.payment_method_id));
          return <span className="text-sm">{name || '-'}</span>;
        },
      },
    ];

    if (hasTeamData) {
      baseColumns.push({
        headerName: t('table.client'),
        field: 'team',
        valueGetter: (params) => params.data.team?.name,
        cellRenderer: (params: any) => {
          const order = params.data as Order;
          return order.team?.name ? (
            <UserCard name={order.team.name} email="" />
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          );
        },
      });
    }

    // 🔵 COLUMNA: Acciones (siempre presente y fijada a la derecha)
    baseColumns.push({
      headerName: '',
      field: 'actions',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right', // Evita que el usuario mueva la columna
      suppressMovable: true, // Deshabilita el drag & drop para esta columna
      suppressSizeToFit: true,
      suppressAutoSize: true,
      // 🎯 CELDA: Enlace a ver pedido
      cellRenderer: (params: any) => {
        const order = params.data as Order;
        const hasValidId = order.id && order.id > 0;

        return (
          <div className="flex items-center justify-center">
            {hasValidId && (
              <Link
                to={`/orders/view/${order.id}`}
                className="text-complementary-text hover:text-text transition-colors inline-flex items-center justify-center p-2"
                title="Ver pedido"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
              </Link>
            )}
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, hasTeamData, paymentMethodMap]);

  // 🎯 Filtrar columnas basado en la selección del ColumnSelector
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    // 🎯 Columnas que siempre deben estar visibles
    const alwaysVisibleColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      // Mantener siempre 'actions' y 'ref' para evitar estados sin columnas de datos
      return field === 'actions' || field === 'ref' || field === 'unknown';
    });

    // 🎯 Columnas seleccionables
    const selectableColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    });

    // Utilidad para evitar columnas duplicadas por `field`
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      const result: ColDef[] = [];
      cols.forEach((c) => {
        const id = (c.field || c.headerName || 'unknown') as string;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(c);
        }
      });
      return result;
    };

    if (selectedColumns.length === 0) {
      // Si no hay selección, mostrar todas las columnas seleccionables + las siempre visibles (sin duplicados)
      return dedupeByField([...selectableColumns, ...alwaysVisibleColumns]);
    }

    const selectedSelectableColumns = selectableColumns.filter((col) => {
      const fieldId = col.field || col.headerName || 'unknown';
      return selectedColumns.includes(fieldId);
    });

    // Combinar columnas seleccionadas + siempre visibles SIN duplicados
    const combined = [...selectedSelectableColumns];
    alwaysVisibleColumns.forEach((c) => {
      const field = c.field || c.headerName || 'unknown';
      const exists = combined.some((x) => (x.field || x.headerName || 'unknown') === field);
      if (!exists) combined.push(c);
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  // 🎯 Columnas para el ColumnSelector (formato TanStack)
  const tanStackColumns: ColumnDef<Order>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
